import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  ShoppingCart,
  LogOut,
  Plus,
  FileText,
  Archive,
  ClipboardList,
  Edit,
  Trash,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

import { UserContext } from "../../context/UserContext";
import { db } from "../../firebase/firebaseConfig";
import LogoutModal from "../../components/modal/LogoutModal";

// ===================== MAIN COMPONENT =====================
const AdminPanel = () => {
  const [activePage, setActivePage] = useState("dashboard");
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [codeshop, setCodeshop] = useState([]);
  const [interviewPreps, setInterviewPreps] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { user, logout } = useContext(UserContext);
  const navigate = useNavigate();

  // ======== FIRESTORE FETCHERS ========
  const fetchAll = async () => {
    const collections = {
      users: "users",
      courses: "courses",
      blogs: "blogs",
      codeshop: "codeshop",
      purchases: "purchases",
      interviewPreps: "interviewPreps",
    };
    for (let [key, name] of Object.entries(collections)) {
      const snap = await getDocs(collection(db, name));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (key === "users") setUsers(data);
      if (key === "courses") setCourses(data);
      if (key === "blogs") setBlogs(data);
      if (key === "codeshop") setCodeshop(data);
      if (key === "purchases") setPurchases(data);
      if (key === "interviewPreps") setInterviewPreps(data);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // ============ DASHBOARD PAGE ============
  const DashboardPage = () => {
    const revenueData = purchases.map((p) => ({
      date: p.createdAt?.toDate().toLocaleDateString(),
      amount: p.amount || 0,
    }));
    const userData = users.map((u) => ({
      date: u.createdAt?.toDate().toLocaleDateString(),
      users: 1,
    }));

    return (
      <>
        <Title>Dashboard Overview</Title>
        <CardGrid>
          <StatCard>
            <h3>{users.length}</h3>
            <p>Total Users</p>
          </StatCard>
          <StatCard>
            <h3>{courses.length}</h3>
            <p>Total Courses</p>
          </StatCard>
          <StatCard>
            <h3>{blogs.length}</h3>
            <p>Total Blogs</p>
          </StatCard>
          <StatCard>
            <h3>{codeshop.length}</h3>
            <p>CodeShop Items</p>
          </StatCard>
          <StatCard>
            <h3>{interviewPreps.length}</h3>
            <p>Interview Preps</p>
          </StatCard>
          <StatCard>
            <h3>{purchases.length}</h3>
            <p>Total Purchases</p>
          </StatCard>
          <StatCard>
            <h3>₹{purchases.reduce((sum, p) => sum + (p.amount || 0), 0)}</h3>
            <p>Total Revenue</p>
          </StatCard>
        </CardGrid>

        <ChartSection>
          <ChartBlock>
            <ChartTitle>Revenue Over Time</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="date" stroke="#aaa" />
                <YAxis stroke="#aaa" />
                <Tooltip />
                <Line type="monotone" dataKey="amount" stroke="#c59fff" />
              </LineChart>
            </ResponsiveContainer>
          </ChartBlock>

          <ChartBlock>
            <ChartTitle>Users Growth</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={userData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="date" stroke="#aaa" />
                <YAxis stroke="#aaa" />
                <Tooltip />
                <Bar dataKey="users" fill="#9b5cff" />
              </BarChart>
            </ResponsiveContainer>
          </ChartBlock>
        </ChartSection>
      </>
    );
  };

  // 🧑‍💻 USERS PAGE
  const UsersPage = () => {
    const handleRoleChange = async (id, newRole, currentRole) => {
      if (newRole === currentRole) return;
      const confirmChange = window.confirm(
        `Change this user's role from "${currentRole}" to "${newRole}"?`
      );
      if (!confirmChange) return;
      await updateDoc(doc(db, "users", id), { role: newRole });
      alert(`✅ Role updated to ${newRole}`);
      fetchAll();
    };

    return (
      <>
        <Title>Users</Title>
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Joined</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{u.createdAt?.toDate().toLocaleDateString()}</td>
                  <td>
                    <select
                      value={u.role || "user"}
                      onChange={(e) =>
                        handleRoleChange(u.id, e.target.value, u.role || "user")
                      }
                      style={{
                        background: "#0b0617",
                        color: "#c59fff",
                        border: "1px solid #9b5cff",
                        padding: "5px 8px",
                        borderRadius: "6px",
                      }}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>
      </>
    );
  };

  // 🎓 COURSES PAGE
  const CoursesPage = () => {
    const handleDelete = async (id) => {
      if (!window.confirm("Delete this course?")) return;
      await deleteDoc(doc(db, "courses", id));
      fetchAll();
    };
    return (
      <>
        <Title>Courses</Title>
        <AddButton onClick={() => navigate("/add-course")}>
          <Plus size={18} /> Add New Course
        </AddButton>
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Title</th>
                <th>Price</th>
                <th>Discount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id}>
                  <td>{c.title}</td>
                  <td>₹{c.price}</td>
                  <td>{c.discount || 0}%</td>
                  <td>
                    <ActionBtn onClick={() => navigate(`/edit-course/${c.id}`)}>
                      <Edit size={14} />
                    </ActionBtn>
                    <DeleteBtn onClick={() => handleDelete(c.id)}>
                      <Trash size={14} />
                    </DeleteBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>
      </>
    );
  };

  // 🧠 INTERVIEW PREPS PAGE
  const InterviewPrepsPage = () => {
    const handleDelete = async (id) => {
      if (!window.confirm("Delete this Interview Prep?")) return;
      await deleteDoc(doc(db, "interviewPreps", id));
      fetchAll();
    };
    return (
      <>
        <Title>Interview Preps</Title>
        <AddButton onClick={() => navigate("/add-interviewprep")}>
          <Plus size={18} /> Add Interview Prep
        </AddButton>
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {interviewPreps.map((i) => (
                <tr key={i.id}>
                  <td
                    className="clickable"
                    onClick={() => navigate(`/interview-prep/${i.id}`)}
                  >
                    {i.title}
                  </td>
                  <td>{i.category || "General"}</td>
                  <td>{i.createdAt?.toDate().toLocaleDateString()}</td>
                  <td>
                    <ActionBtn
                      onClick={() => navigate(`/edit-interviewprep/${i.id}`)}
                    >
                      <Edit size={14} />
                    </ActionBtn>
                    <DeleteBtn onClick={() => handleDelete(i.id)}>
                      <Trash size={14} />
                    </DeleteBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>
      </>
    );
  };

  // 📄 BLOGS PAGE
  const BlogsPage = () => {
    const handleDelete = async (id) => {
      if (!window.confirm("Delete this blog?")) return;
      await deleteDoc(doc(db, "blogs", id));
      fetchAll();
    };
    return (
      <>
        <Title>Blogs</Title>
        <AddButton onClick={() => navigate("/add-blog")}>
          <Plus size={18} /> Add New Blog
        </AddButton>
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Title</th>
                <th>Author</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map((b) => (
                <tr key={b.id}>
                  <td>{b.title}</td>
                  <td>{b.author || "Unknown"}</td>
                  <td>{b.createdAt?.toDate().toLocaleDateString()}</td>
                  <td>
                    <ActionBtn onClick={() => navigate(`/edit-blog/${b.id}`)}>
                      <Edit size={14} />
                    </ActionBtn>
                    <DeleteBtn onClick={() => handleDelete(b.id)}>
                      <Trash size={14} />
                    </DeleteBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>
      </>
    );
  };

  // 💾 CODESHOP PAGE
  const CodeShopPage = () => {
    const handleDelete = async (id) => {
      if (!window.confirm("Delete this code item?")) return;
      await deleteDoc(doc(db, "codeshop", id));
      fetchAll();
    };
    return (
      <>
        <Title>Code Shop</Title>
        <AddButton onClick={() => navigate("/add-codeshop")}>
          <Plus size={18} /> Add Code Item
        </AddButton>
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Title</th>
                <th>Price</th>
                <th>Date</th>
                <th>File</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codeshop.map((c) => (
                <tr key={c.id}>
                  <td>{c.title}</td>
                  <td>{c.price === 0 ? "Free" : `₹${c.price}`}</td>
                  <td>{c.createdAt?.toDate().toLocaleDateString()}</td>
                  <td>
                    <a
                      href={c.fileURL}
                      download
                      style={{ color: "#c59fff", textDecoration: "underline" }}
                    >
                      Download
                    </a>
                  </td>
                  <td>
                    <ActionBtn
                      onClick={() => navigate(`/edit-codeshop/${c.id}`)}
                    >
                      <Edit size={14} />
                    </ActionBtn>
                    <DeleteBtn onClick={() => handleDelete(c.id)}>
                      <Trash size={14} />
                    </DeleteBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>
      </>
    );
  };

  // 💰 PURCHASES PAGE
  const PurchasesPage = () => (
    <>
      <Title>Purchases</Title>
      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th>User</th>
              <th>Course</th>
              <th>Amount</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((p) => (
              <tr key={p.id}>
                <td>{p.userEmail}</td>
                <td>{p.courseTitle}</td>
                <td>₹{p.amount}</td>
                <td>{p.createdAt?.toDate().toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableWrapper>
    </>
  );

  // =================== RENDER ===================
  return (
    <Wrapper>
      {/* Sidebar */}
      <Sidebar>
        <Logo>
          <img src="/logo.png" alt="SolveMyCodeIssue Admin" />
        </Logo>
        {[
          { name: "dashboard", label: "Dashboard", icon: LayoutDashboard },
          { name: "users", label: "Users", icon: Users },
          { name: "courses", label: "Courses", icon: BookOpen },
          { name: "blogs", label: "Blogs", icon: FileText },
          { name: "codeshop", label: "Code Shop", icon: Archive },
          {
            name: "interviewPreps",
            label: "Interview Preps",
            icon: ClipboardList,
          },
          { name: "purchases", label: "Purchases", icon: ShoppingCart },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <SidebarItem
              key={item.name}
              active={activePage === item.name}
              onClick={() => setActivePage(item.name)}
            >
              <Icon size={18} /> {item.label}
            </SidebarItem>
          );
        })}
        <LogoutItem onClick={() => setShowLogoutModal(true)}>
          <LogOut size={18} /> Logout
        </LogoutItem>
      </Sidebar>

      {/* Content */}
      <MainContent>
        {activePage === "dashboard" && <DashboardPage />}
        {activePage === "users" && <UsersPage />}
        {activePage === "courses" && <CoursesPage />}
        {activePage === "blogs" && <BlogsPage />}
        {activePage === "codeshop" && <CodeShopPage />}
        {activePage === "interviewPreps" && <InterviewPrepsPage />}
        {activePage === "purchases" && <PurchasesPage />}
      </MainContent>

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          setShowLogoutModal(false);
          logout();
          navigate("/");
        }}
      />
    </Wrapper>
  );
};

export default AdminPanel;

//
// ===================== STYLED COMPONENTS =====================
//
const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  font-family: "Inter", sans-serif;
`;

const Sidebar = styled.div`
  width: 250px;
  background: rgba(20, 10, 35, 0.9);
  border-right: 1px solid rgba(155, 92, 255, 0.3);
  padding: 25px 20px;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(12px);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 30px;
  img {
    width: 120px;
    border-radius: 12px;
  }
`;

const SidebarItem = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: 0.3s;
  background: ${({ active }) =>
    active ? "linear-gradient(90deg, #9b5cff, #c59fff)" : "transparent"};
  color: ${({ active }) => (active ? "#fff" : "#ccc")};
  font-weight: ${({ active }) => (active ? "600" : "400")};
  &:hover {
    color: #c59fff;
    background: rgba(155, 92, 255, 0.1);
  }
`;

const LogoutItem = styled(SidebarItem)`
  margin-top: auto;
  color: #ff6b6b;
  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 40px;
  overflow-y: auto;
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 30px 0;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 14px;
  padding: 25px;
  text-align: center;
  box-shadow: 0 0 15px rgba(155, 92, 255, 0.15);
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.4);
  }
  h3 {
    font-size: 2rem;
    color: #c59fff;
  }
  p {
    color: #aaa;
  }
`;

const AddButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  border: none;
  color: white;
  padding: 12px 20px;
  border-radius: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.4);
  }
`;

const TableWrapper = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  padding: 20px;
  margin-top: 30px;
  border: 1px solid rgba(155, 92, 255, 0.3);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  th {
    color: #c59fff;
    text-align: left;
  }

  td {
    color: #ddd;
    transition: color 0.2s ease;
  }

  .clickable {
    color: #c59fff;
    cursor: pointer;
    text-decoration: underline;
  }

  .clickable:hover {
    color: #fff;
    text-shadow: 0 0 8px rgba(155, 92, 255, 0.6);
  }
`;

const ChartSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 50px;
`;

const ChartBlock = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  border: 1px solid rgba(155, 92, 255, 0.3);
  padding: 20px;
`;

const ChartTitle = styled.h3`
  color: #c59fff;
  margin-bottom: 15px;
  font-size: 1.1rem;
`;

const ActionBtn = styled.button`
  background: #9b5cff;
  color: white;
  border: none;
  margin-right: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  &:hover {
    background: #c59fff;
  }
`;

const DeleteBtn = styled(ActionBtn)`
  background: #ff5c5c;
  &:hover {
    background: #e03b3b;
  }
`;
